<template>
  <div class="manpower-report">
    <Manpower_bar />
    
    <v-container fluid class="pa-6">
      <!-- Header Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <div class="header-section">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div>
                <h1 class="page-title">
                  <v-icon large color="primary" class="mr-3">mdi-chart-timeline-variant</v-icon>
                  รายงานอัตรากำลังประจำปี
                </h1>
                <p class="page-subtitle mt-2">ระบบติดตามและรายงานอัตรากำลังสถานศึกษา สังกัด สอศ.</p>
              </div>
              <div class="d-flex align-center">
                <v-chip class="mr-3" color="primary" outlined>
                  <v-icon left small>mdi-calendar</v-icon>
                  ปีงบประมาณ {{ years_select }}
                </v-chip>
                <v-chip :color="statusColor" dark>
                  <v-icon left small>mdi-database</v-icon>
                  {{ rate_work_gs.length }} สถานศึกษา
                </v-chip>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Statistics Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-success" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">ดำเนินการเรียบร้อย</p>
                  <h2 class="stat-value">{{ statistics.confirmed }}</h2>
                  <p class="stat-percent">{{ statistics.confirmedPercent }}%</p>
                </div>
                <v-avatar size="56" color="success" class="stat-icon">
                  <v-icon dark size="28">mdi-checkbox-marked-circle</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="statistics.confirmedPercent" 
                color="success" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-warning" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">อยู่ระหว่างดำเนินการ</p>
                  <h2 class="stat-value">{{ statistics.inProgress }}</h2>
                  <p class="stat-percent">{{ statistics.inProgressPercent }}%</p>
                </div>
                <v-avatar size="56" color="warning" class="stat-icon">
                  <v-icon dark size="28">mdi-progress-clock</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="statistics.inProgressPercent" 
                color="warning" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-error" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">ยังไม่ได้ดำเนินการ</p>
                  <h2 class="stat-value">{{ statistics.pending }}</h2>
                  <p class="stat-percent">{{ statistics.pendingPercent }}%</p>
                </div>
                <v-avatar size="56" color="error" class="stat-icon">
                  <v-icon dark size="28">mdi-alert-circle</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="statistics.pendingPercent" 
                color="error" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-info" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">รวมทั้งหมด</p>
                  <h2 class="stat-value">{{ statistics.total }}</h2>
                  <p class="stat-percent">สถานศึกษา</p>
                </div>
                <v-avatar size="56" color="info" class="stat-icon">
                  <v-icon dark size="28">mdi-school</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="100" 
                color="info" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Section -->
      <v-row class="mb-6">
        <v-col cols="12" md="5">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <v-icon color="primary" class="mr-2">mdi-chart-donut</v-icon>
              สถานะการรายงาน
            </v-card-title>
            <v-card-text>
              <apexchart
                ref="donutChart"
                type="donut"
                height="320"
                :options="donutChartOptions"
                :series="donutSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
              สถิติตามประเภทสถานศึกษา
            </v-card-title>
            <v-card-text>
              <apexchart
                ref="barChart"
                type="bar"
                height="320"
                :options="barChartOptions"
                :series="barSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Content -->
      <v-row>
        <!-- Control Panel -->
        <v-col cols="12" md="3">
          <v-card class="control-panel" elevation="4">
            <v-card-title class="control-title">
              <v-icon color="primary" class="mr-2">mdi-tune-vertical</v-icon>
              แผงควบคุม
            </v-card-title>
            <v-card-text>
              <!-- Year Selection -->
              <v-select
                v-model="years_select"
                :items="year_ss"
                label="เลือกปีงบประมาณ"
                prepend-inner-icon="mdi-calendar"
                outlined
                dense
                class="mb-4"
                @change="rate_work_gQueryTime()"
              >
                <template v-slot:item="{ item }">
                  <span>พ.ศ. {{ item }}</span>
                </template>
                <template v-slot:selection="{ item }">
                  <span>พ.ศ. {{ item }}</span>
                </template>
              </v-select>

              <v-divider class="mb-4"></v-divider>

              <!-- Filter Buttons -->
              <p class="filter-label mb-3">
                <v-icon small class="mr-1">mdi-filter</v-icon>
                กรองตามสถานะ
              </p>
              
              <v-btn
                @click="checkConfirm()"
                block
                class="mb-2 filter-btn"
                :color="activeFilter === 'confirm' ? 'success' : ''"
                :outlined="activeFilter !== 'confirm'"
              >
                <v-icon left>mdi-checkbox-marked-circle</v-icon>
                ดำเนินการเรียบร้อย
              </v-btn>

              <v-btn
                @click="checkSave()"
                block
                class="mb-2 filter-btn"
                :color="activeFilter === 'save' ? 'warning' : ''"
                :outlined="activeFilter !== 'save'"
              >
                <v-icon left>mdi-progress-clock</v-icon>
                อยู่ระหว่างดำเนินการ
              </v-btn>

              <v-btn
                @click="checkNull()"
                block
                class="mb-2 filter-btn"
                :color="activeFilter === 'null' ? 'error' : ''"
                :outlined="activeFilter !== 'null'"
              >
                <v-icon left>mdi-alert-circle</v-icon>
                ยังไม่ได้ดำเนินการ
              </v-btn>

              <v-btn
                @click="rate_work_gQueryTime()"
                block
                class="mb-4 filter-btn"
                :color="activeFilter === 'all' ? 'primary' : ''"
                :outlined="activeFilter !== 'all'"
              >
                <v-icon left>mdi-view-grid</v-icon>
                แสดงทั้งหมด
              </v-btn>

              <v-divider class="mb-4"></v-divider>

              <!-- Action Buttons -->
              <p class="filter-label mb-3">
                <v-icon small class="mr-1">mdi-cog</v-icon>
                การดำเนินการ
              </p>

              <v-btn
                @click="rate_work_gUpdateAuto()"
                block
                color="primary"
                dark
                class="mb-2"
                :loading="loading"
              >
                <v-icon left>mdi-sync</v-icon>
                ปรับปรุงข้อมูลอัตโนมัติ
              </v-btn>

              <v-divider class="my-4"></v-divider>

              <!-- Reports Section -->
              <p class="filter-label mb-3">
                <v-icon small class="mr-1">mdi-file-document</v-icon>
                รายงาน
              </p>

              <v-list dense class="report-list">
                <v-list-item
                  v-for="(item, i) in items"
                  :key="i"
                  :href="item.link + years_select"
                  target="_blank"
                  class="report-item"
                >
                  <v-list-item-icon>
                    <v-icon color="primary">{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="report-text">{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Data Table -->
        <v-col cols="12" md="9">
          <v-card class="data-table-card" elevation="4">
            <v-card-title class="table-title d-flex justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-table</v-icon>
                <span>ข้อมูลอัตรากำลังสถานศึกษา</span>
              </div>
              <div class="d-flex align-center table-actions">
                <v-btn
                  color="success"
                  class="mr-2"
                  @click="exportToExcel()"
                  :disabled="!rate_work_gs.length"
                >
                  <v-icon left>mdi-microsoft-excel</v-icon>
                  ส่งออก Excel
                </v-btn>                
              </div>
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหาสถานศึกษา..."
                outlined
                dense
                clearable
                hide-details
                class="mb-4 search-field"
              ></v-text-field>

              <v-data-table
                :loading="loading"
                :headers="headers"
                :items="rate_work_gs"
                :search="search"
                :items-per-page="15"
                :footer-props="{
                  'items-per-page-options': [10, 15, 25, 50, -1],
                  'items-per-page-text': 'แสดงรายการ:'
                }"
                class="modern-table"
                loading-text="กำลังโหลดข้อมูล..."
                no-data-text="ไม่พบข้อมูล"
                no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
              >
                <template v-slot:item.rate_work_g_status="{ item }">
                  <v-chip
                    small
                    :color="getStatusColor(item.rate_work_g_status)"
                    dark
                    class="status-chip"
                  >
                    <v-icon left x-small>{{ getStatusIcon(item.rate_work_g_status) }}</v-icon>
                    {{ getStatusText(item.rate_work_g_status) }}
                  </v-chip>
                </template>

                <template v-slot:item.rate_work_g_college_code="{ item }">
                  <span class="college-code">{{ item.rate_work_g_college_code }}</span>
                </template>

                <template v-slot:item.college_name="{ item }">
                  <div class="college-name">
                    <strong>{{ item.college_name }}</strong>
                    <br>
                    <small class="grey--text">{{ item.province_name }}</small>
                  </div>
                </template>

                <template v-slot:item.std_vc="{ item }">
                  <span class="number-cell">{{ formatNumber(item.std_vc) }}</span>
                </template>

                <template v-slot:item.std_hvc="{ item }">
                  <span class="number-cell">{{ formatNumber(item.std_hvc) }}</span>
                </template>

                <template v-slot:item.std_sc="{ item }">
                  <span class="number-cell">{{ formatNumber(item.std_sc) }}</span>
                </template>

                <template v-slot:item.rate_criterion_all="{ item }">
                  <v-chip small color="primary" dark>
                    {{ formatNumber(item.rate_criterion_all) }}
                  </v-chip>
                </template>

                <template v-slot:item.rate_criterion_incomplete_all="{ item }">
                  <v-chip 
                    small 
                    :color="item.rate_criterion_incomplete_all > 0 ? 'error' : 'success'" 
                    dark
                  >
                    {{ item.rate_criterion_incomplete_all > 0 ? '-' : '' }}{{ Math.abs(item.rate_criterion_incomplete_all) }}
                  </v-chip>
                </template>

                <template v-slot:item.action_s="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="rate_work_gDelete(item.rate_work_g_year, item.rate_work_g_college_code)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>ยกเลิกรายงาน</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleterate_work_gdialog" max-width="500" persistent>
        <v-card class="delete-dialog">
          <v-card-title class="error white--text">
            <v-icon left dark>mdi-alert-circle</v-icon>
            ยืนยันการลบข้อมูล
          </v-card-title>
          <v-card-text class="pt-4">
            <v-alert type="warning" dense outlined class="mb-4">
              การลบข้อมูลจะไม่สามารถกู้คืนได้
            </v-alert>
            <p class="text-body-1">
              คุณต้องการลบข้อมูลรายงานของ<br>
              <strong>{{ editrate_work_g.college_name }}</strong><br>
              ประจำปี <strong>{{ editrate_work_g.rate_work_g_year }}</strong> หรือไม่?
            </p>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="deleterate_work_gdialog = false">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn color="error" @click="deleterate_work_gSubmit()" :loading="loading">
              <v-icon left>mdi-delete</v-icon>
              ยืนยันลบ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        <div class="d-flex align-center">
          <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
          {{ snackbar.text }}
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import Manpower_bar from "../../components/admin/manpower_bar.vue";
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";

export default {
  name: "ManpowerReport",
  
  components: { 
    Manpower_bar,
    apexchart: VueApexCharts
  },

  data() {
    const currentYear = new Date().getFullYear() + 543; // Convert to Buddhist year
    
    return {
      loading: false,
      ApiKey: "HRvec2021",
      years_select: currentYear,
      year_ss: this.generateYearOptions(),
      valid: true,
      activeFilter: "all",
      
      // Dialogs
      addrate_work_gdialog: false,
      editrate_work_gdialog: false,
      deleterate_work_gdialog: false,
      deleterate_work_gPersondialog: false,
      
      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },
      
      // Data
      rate_work_gs: [],
      addrate_work_g: {},
      editrate_work_g: {},
      colleges: [],
      userstatus: [],
      search: "",
      
      // Table Headers
      headers: [
        { text: "สถานะ", align: "center", value: "rate_work_g_status", width: "120" },
        { text: "รหัส", align: "center", value: "rate_work_g_college_code", width: "100" },
        { text: "สถานศึกษา", align: "left", value: "college_name", width: "250" },
        { text: "ปี", align: "center", value: "rate_work_g_year", width: "80" },
        { text: "ประเภท", align: "center", value: "name_ctm", width: "100" },
        { text: "นร.ปวช.", align: "right", value: "std_vc", width: "100" },
        { text: "นศ.ปวส.", align: "right", value: "std_hvc", width: "100" },
        { text: "นศ.ระยะสั้น", align: "right", value: "std_sc", width: "100" },
        { text: "เกณฑ์รวม", align: "center", value: "rate_criterion_all", width: "100" },
        { text: "ขาด/เกิน", align: "center", value: "rate_criterion_incomplete_all", width: "100" },
        { text: "จัดการ", align: "center", value: "action_s", width: "80", sortable: false }
      ],
      
      // Report Links
      items: [
        {
          text: "ข้อมูลสถานศึกษา",
          icon: "mdi-home-city",
          link: "#/admin/print_report_rate_work_g/"
        },
        {
          text: "อัตรากำลังครูผู้สอน",
          icon: "mdi-human-male-board",
          link: "#/admin/print_report_rate_work_g_teach/"
        },
        {
          text: "อัตรากำลังผู้บริหาร",
          icon: "mdi-account-tie",
          link: "#/admin/print_report_rate_work_g_dr/"
        },
        {
          text: "สายสนับสนุนการสอน",
          icon: "mdi-account-group",
          link: "#/admin/print_report_rate_work_g_sup/"
        },
        {
          text: "ลูกจ้างทั่วไป",
          icon: "mdi-account-hard-hat",
          link: "#/admin/print_report_rate_work_g_pte/"
        }
      ],
      
      // Update objects
      updaterate_work_college: {},
      deleterate_work_college_cal_room: {},
      updaterate_work_g: {},
      updaterate_work_sc: {},

      // Chart Options
      donutChartOptions: {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        labels: ["ดำเนินการเรียบร้อย", "อยู่ระหว่างดำเนินการ", "ยังไม่ดำเนินการ"],
        colors: ["#B3151B", "#424242", "#E57373"],
        legend: {
          position: "bottom",
          fontSize: "14px",
          fontFamily: "Sarabun, sans-serif",
          markers: {
            width: 12,
            height: 12,
            radius: 2
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "16px",
                  fontFamily: "Sarabun, sans-serif"
                },
                value: {
                  show: true,
                  fontSize: "24px",
                  fontFamily: "Sarabun, sans-serif",
                  fontWeight: "bold"
                },
                total: {
                  show: true,
                  label: "รวมทั้งหมด",
                  fontSize: "14px",
                  fontFamily: "Sarabun, sans-serif",
                  formatter: function(w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0) + " แห่ง";
                  }
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " สถานศึกษา";
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 280
            },
            legend: {
              position: "bottom"
            }
          }
        }]
      },

      barChartOptions: {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          stacked: true,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: "60%"
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "Sarabun, sans-serif"
            }
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (สถานศึกษา)",
            style: {
              fontSize: "12px",
              fontFamily: "Sarabun, sans-serif"
            }
          }
        },
        colors: ["#B3151B", "#424242", "#E57373"],
        legend: {
          position: "top",
          fontSize: "14px",
          fontFamily: "Sarabun, sans-serif",
          markers: {
            width: 12,
            height: 12,
            radius: 2
          }
        },
        fill: {
          opacity: 1
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " สถานศึกษา";
            }
          }
        }
      }
    };
  },

  computed: {
    statistics() {
      const total = this.rate_work_gs.length;
      const confirmed = this.rate_work_gs.filter(item => item.rate_work_g_status === "Confirm").length;
      const inProgress = this.rate_work_gs.filter(item => item.rate_work_g_status === "Save").length;
      const pending = total - confirmed - inProgress;
      
      return {
        total,
        confirmed,
        inProgress,
        pending,
        confirmedPercent: total > 0 ? ((confirmed / total) * 100).toFixed(1) : 0,
        inProgressPercent: total > 0 ? ((inProgress / total) * 100).toFixed(1) : 0,
        pendingPercent: total > 0 ? ((pending / total) * 100).toFixed(1) : 0
      };
    },
    
    donutSeries() {
      return [
        this.statistics.confirmed,
        this.statistics.inProgress,
        this.statistics.pending
      ];
    },
    
    barSeries() {
      const typeStats = this.calculateTypeStatistics();
      return [
        { name: "ดำเนินการเรียบร้อย", data: typeStats.confirmed },
        { name: "อยู่ระหว่างดำเนินการ", data: typeStats.inProgress },
        { name: "ยังไม่ดำเนินการ", data: typeStats.pending }
      ];
    },
    
    statusColor() {
      const percent = parseFloat(this.statistics.confirmedPercent);
      if (percent >= 80) return "success";
      if (percent >= 50) return "warning";
      return "error";
    }
  },

  watch: {
    rate_work_gs: {
      handler() {
        this.updateBarChart();
      },
      deep: true
    }
  },

  async mounted() {
    await this.rate_work_gQueryTime();
  },

  methods: {
    generateYearOptions() {
      const currentYear = new Date().getFullYear() + 543;
      const years = [];
      for (let i = currentYear - 3; i <= currentYear + 3; i++) {
        years.push(i);
      }
      return years;
    },
    
    calculateTypeStatistics() {
      const types = [...new Set(this.rate_work_gs.map(item => item.type_college || "อื่นๆ"))];
      const confirmed = [];
      const inProgress = [];
      const pending = [];
      
      types.forEach(type => {
        const typeItems = this.rate_work_gs.filter(item => (item.type_college || "อื่นๆ") === type);
        confirmed.push(typeItems.filter(item => item.rate_work_g_status === "Confirm").length);
        inProgress.push(typeItems.filter(item => item.rate_work_g_status === "Save").length);
        pending.push(typeItems.filter(item => item.rate_work_g_status !== "Confirm" && item.rate_work_g_status !== "Save").length);
      });
      
      return { types, confirmed, inProgress, pending };
    },
    
    updateBarChart() {
      const typeStats = this.calculateTypeStatistics();
      if (this.$refs.barChart) {
        this.$refs.barChart.updateOptions({
          xaxis: {
            categories: typeStats.types
          }
        });
      }
    },
    
    getStatusColor(status) {
      switch (status) {
        case "Confirm": return "success";
        case "Save": return "warning";
        default: return "error";
      }
    },
    
    getStatusIcon(status) {
      switch (status) {
        case "Confirm": return "mdi-check-circle";
        case "Save": return "mdi-progress-clock";
        default: return "mdi-alert-circle";
      }
    },
    
    getStatusText(status) {
      switch (status) {
        case "Confirm": return "เสร็จสิ้น";
        case "Save": return "กำลังดำเนินการ";
        default: return "รอดำเนินการ";
      }
    },
    
    formatNumber(num) {
      if (num === null || num === undefined) return "-";
      return Number(num).toLocaleString("th-TH");
    },
    
    showSnackbar(color, icon, text) {
      this.snackbar = { show: true, color, icon, text, timeout: 5000 };
    },
    
    // API Methods
    async rate_work_gUpdateAuto() {
      try {
        this.loading = true;
        let result = await this.$http.post("rate_work_g_updateAuto.php", {
          ApiKey: this.ApiKey
        });
        if (result.data.status === true) {
          this.showSnackbar("success", "mdi-check-circle", "ปรับปรุงข้อมูลอัตโนมัติสำเร็จ");
          await this.rate_work_gQueryTime();
        }
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการปรับปรุงข้อมูล");
      } finally {
        this.loading = false;
      }
    },

    async rate_work_gQueryTime() {
      this.loading = true;
      this.activeFilter = "all";
      try {
        let result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          rate_work_g_year: this.years_select
        });
        this.rate_work_gs = result.data || [];
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        this.loading = false;
      }
    },

    async checkConfirm() {
      this.loading = true;
      this.activeFilter = "confirm";
      try {
        let result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          rate_work_g_year: this.years_select,
          checkConfirm: "OK"
        });
        this.rate_work_gs = result.data || [];
      } finally {
        this.loading = false;
      }
    },

    async checkSave() {
      this.loading = true;
      this.activeFilter = "save";
      try {
        let result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          rate_work_g_year: this.years_select,
          checkSave: "OK"
        });
        this.rate_work_gs = result.data || [];
      } finally {
        this.loading = false;
      }
    },

    async checkNull() {
      this.loading = true;
      this.activeFilter = "null";
      try {
        let result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          checkNull: "OK"
        });
        this.rate_work_gs = result.data || [];
      } finally {
        this.loading = false;
      }
    },

    async rate_work_gDelete(rate_work_g_year, rate_work_g_college_code) {
      try {
        let result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          rate_work_g_year: rate_work_g_year,
          rate_work_g_college_code: rate_work_g_college_code,
          rate_work_g_year_admin: "Check"
        });
        this.editrate_work_g = result.data;
        this.deleterate_work_gdialog = true;
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการโหลดข้อมูล");
      }
    },

    async deleterate_work_gSubmit() {
      this.loading = true;
      try {
        this.updaterate_work_college = {
          ApiKey: this.ApiKey,
          rate_work_college_year: this.editrate_work_g.rate_work_g_year,
          rate_work_college_code: this.editrate_work_g.college_code,
          rate_work_college_status: ""
        };

        this.deleterate_work_college_cal_room = {
          ApiKey: this.ApiKey,
          rate_work_ccr_year: this.editrate_work_g.rate_work_g_year,
          rate_work_ccr_code: this.editrate_work_g.college_code
        };

        this.updaterate_work_g = {
          ApiKey: this.ApiKey,
          rate_work_g_year: this.editrate_work_g.rate_work_g_year,
          rate_work_g_college_code: this.editrate_work_g.college_code
        };

        this.updaterate_work_sc = {
          ApiKey: this.ApiKey,
          rate_sc_year: this.editrate_work_g.rate_work_g_year,
          rate_sc_college_code: this.editrate_work_g.college_code,
          rate_sc_status: ""
        };

        await this.$http.post("rate_work_college.update.admin.php", this.updaterate_work_college);
        await this.$http.post("rate_work_college_cal_room.admin.delete.php", this.deleterate_work_college_cal_room);
        let result = await this.$http.post("rate_work_g.delete.php", this.updaterate_work_g);
        await this.$http.post("rate_work_sc.update.admin.php", this.updaterate_work_sc);

        if (result.data.status === true) {
          this.showSnackbar("success", "mdi-check-circle", "ลบข้อมูลเรียบร้อยแล้ว");
          await this.rate_work_gQueryTime();
        } else {
          this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการลบข้อมูล");
        }
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการลบข้อมูล");
      } finally {
        this.deleterate_work_gdialog = false;
        this.loading = false;
      }
    },

    // Export Methods
    exportToExcel() {
      const exportData = this.rate_work_gs.map(item => ({
        "สถานะ": this.getStatusText(item.rate_work_g_status),
        "รหัสสถานศึกษา": item.rate_work_g_college_code,
        "สถานศึกษา": item.college_name,
        "จังหวัด": item.province_name,
        "ปี": item.rate_work_g_year,
        "ประเภท": item.college_id_code_type_manpower,
        "นร.ปวช.": item.std_vc,
        "นศ.ปวส.": item.std_hvc,
        "นศ.ระยะสั้น": item.std_sc,
        "ห้องรวม": item.rate_room_cal_all,
        "เกณฑ์รวม": item.rate_criterion_all,
        "ผอ. ขาด/เกิน": item.rate_criterion_incomplete_dr,
        "รอง ขาด/เกิน": item.rate_criterion_incomplete_sedr,
        "ครู ขาด/เกิน": item.rate_criterion_incomplete_teach_all,
        "สนับสนุน ขาด/เกิน": item.rate_work_g_sup_incomplete,
        "รวม ขาด/เกิน": item.rate_criterion_incomplete_all
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "รายงานอัตรากำลัง");
      
      // Auto-adjust column widths
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length * 2, 15)
      }));
      ws["!cols"] = colWidths;
      
      XLSX.writeFile(wb, `รายงานอัตรากำลัง_${this.years_select}.xlsx`);
      this.showSnackbar("success", "mdi-check-circle", "ส่งออกไฟล์ Excel สำเร็จ");
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap');

.manpower-report {
  font-family: 'Sarabun', sans-serif;
  background: linear-gradient(135deg, #f5f5f5 0%, #e4e8ec 100%);
  min-height: 100vh;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #d9777b 0%, #b3151b 100%);
  border-radius: 16px;
  padding: 24px 32px;
  color: white;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
}

/* Statistics Cards */
.stat-card {
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.stat-card-success {
  border-left-color: #2E7D32;
}

.stat-card-warning {
  border-left-color: #A86400;
}

.stat-card-error {
  border-left-color: #F44336;
}

.stat-card-info {
  border-left-color: #455A64;
}

.stat-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.stat-percent {
  font-size: 0.875rem;
  color: #999;
  margin: 0;
}

.stat-icon {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Chart Cards */
.chart-card {
  border-radius: 16px;
  overflow: hidden;
}

.chart-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}

/* Control Panel */
.control-panel {
  border-radius: 16px;
  position: sticky;
  top: 20px;
}

.control-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-btn {
  justify-content: flex-start;
  text-transform: none;
  font-weight: 500;
}

.report-list {
  background: transparent;
}

.report-item {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.report-item:hover {
  background: #f5f5f5;
}

.report-text {
  font-size: 0.875rem;
}

/* Data Table Card */
.data-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.table-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  padding: 16px 24px;
}

.table-actions {
  gap: 8px;
}

.search-field {
  max-width: 400px;
}

/* Modern Table Styles */
.modern-table {
  border-radius: 8px;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.modern-table >>> th {
  background: #f8f9fa !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  color: #333 !important;
  white-space: nowrap;
}

.modern-table >>> td {
  font-size: 0.875rem;
  padding: 12px 16px !important;
}

.modern-table >>> tr:hover {
  background: #fbecec !important;
}

.status-chip {
  font-weight: 500;
}

.college-code {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.8rem;
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
}

.college-name {
  line-height: 1.4;
}

.number-cell {
  font-family: 'Roboto Mono', monospace;
  font-weight: 500;
}

/* Delete Dialog */
.delete-dialog {
  border-radius: 16px;
  overflow: hidden;
}

/* Responsive */
@media (max-width: 960px) {
  .header-section {
    padding: 16px 20px;
  }
  
  .page-title {
    font-size: 1.25rem;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
  
  .table-actions {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }
  
  .control-panel {
    position: relative;
    top: 0;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card,
.chart-card,
.control-panel,
.data-table-card {
  animation: fadeIn 0.5s ease forwards;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }
</style>